import React, { useCallback, useEffect, useState } from "react";
import { Grid, TextField } from "@material-ui/core";
import { regExCobraEmail, regExCobraMobile } from "../../../constants";
import { useCommonWidths } from "../../../hooks";
import { useOnboardingStyles } from "./styles";
import { OnboardingHeader } from "./OnboardingHeader";
import { CommonOnboardingProps } from "./Onboarding";
import { NavigationButtons } from "./NavigationButtons";

type InputField = { required: boolean; value: string };

type ContactInfoDataModel = {
	email: string;
	mobilePhone: string;
};

type ContactFormModel = Record<keyof ContactInfoDataModel, InputField>;

const fieldNameTranslations: Record<keyof ContactInfoDataModel, string> = {
	email: "e-post",
	mobilePhone: "mobilnummer",
};

export const OnboardingContactInfo = (props: CommonOnboardingProps) => {
	const { completeOnboardingPage, onboardingStateHandler } = props;

	const classes = useOnboardingStyles();
	const isMobile = useCommonWidths().isSmDown;

	const [contactInfoState, setContactInfoState] = useState<ContactFormModel>({
		email: {
			required: true,
			value: onboardingStateHandler.state.contactInfo.email,
		},
		mobilePhone: {
			required: true,
			value: onboardingStateHandler.state.contactInfo.mobilePhone,
		},
	});
	const [errors, setErrors] = useState<ContactInfoDataModel>({
		email: "",
		mobilePhone: "",
	});

	const handleChangeManual = (fieldName: string, newValue: string) => {
		const key = fieldName as keyof ContactFormModel;
		setContactInfoState({
			...contactInfoState,
			[key]: { ...contactInfoState[key], value: newValue },
		});

		// Clearing errors on input:
		let updatedErrors = { ...errors };
		if (newValue) updatedErrors[key] = "";
		setErrors(updatedErrors);
	};

	const formHasErrors = useCallback((): boolean => {
		let hasErrors = false;
		const missingFields = Object.entries(contactInfoState).filter((x) => {
			const { required, value } = x[1];
			return required && !value;
		});
		let newErrors = { ...errors };
		if (missingFields.length > 0) {
			missingFields.forEach((entry) => {
				const fieldName = entry[0] as keyof ContactInfoDataModel;
				newErrors = {
					...newErrors,
					[fieldName]: `* Påkrevd felt. Vennligst fyll ut ${fieldNameTranslations[fieldName]}`,
				};
			});
			hasErrors = true;
		}
		if (
			contactInfoState.email.value &&
			!new RegExp(regExCobraEmail).test(contactInfoState.email.value)
		) {
			newErrors = {
				...newErrors,
				email: "Vennligst skriv inn en gyldig epostadresse",
			};
			hasErrors = true;
		}
		if (
			contactInfoState.mobilePhone.value &&
			!new RegExp(regExCobraMobile).test(contactInfoState.mobilePhone.value)
		) {
			newErrors = {
				...newErrors,
				mobilePhone:
					"Mobilnummeret må være minst 8 tegn, og kan ikke inneholde spesialtegn, bokstaver, eller starte med 0",
			};
			hasErrors = true;
		}
		setErrors(newErrors);
		return hasErrors;
	}, [errors, contactInfoState]);

	const handleSubmit = async () => {
		if (formHasErrors()) return;

		completeOnboardingPage();
	};

	// Update `formStateHandler`'s ContactInfo state from <Onboarding /> component whenever 'contactInfoState' changes
	useEffect(() => {
		onboardingStateHandler.update({
			...onboardingStateHandler.state,
			contactInfo: {
				email: contactInfoState.email.value,
				mobilePhone: contactInfoState.mobilePhone.value,
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contactInfoState]); // [contactInfoData] should be the only dependency

	return (
		<Grid container spacing={3}>
			<OnboardingHeader
				isMobile={isMobile}
				subtitle="Vennligst oppdater din kontaktinformasjon"
			/>
			<div className={classes.formWrapper}>
				<Grid container item xs={12} justify="center">
					<Grid
						container
						item
						xs={isMobile ? 12 : 8}
						justify="space-between"
						spacing={3}
					>
						<Grid item xs={isMobile ? 12 : 8}>
							<TextField
								name="email"
								label="E-post"
								type="email"
								InputLabelProps={{ className: classes.fieldLabel }}
								error={Boolean(errors.email)}
								helperText={errors.email ?? ""}
								variant="outlined"
								fullWidth
								onChange={(e) =>
									handleChangeManual(e.target.name, e.target.value)
								}
								value={contactInfoState.email.value}
							/>
						</Grid>
						<Grid item xs={isMobile ? 12 : 8}>
							<TextField
								name="mobilePhone"
								label="Mobilnummer"
								InputLabelProps={{ className: classes.fieldLabel }}
								error={Boolean(errors.mobilePhone)}
								helperText={errors.mobilePhone ?? ""}
								variant="outlined"
								fullWidth
								onChange={(e) =>
									handleChangeManual(e.target.name, e.target.value)
								}
								value={contactInfoState.mobilePhone.value}
							/>
						</Grid>
					</Grid>

					<NavigationButtons
						handleNavigateNext={handleSubmit}
						handleNavigatePrev={undefined}
						nextDisabled={Boolean(errors.email) || Boolean(errors.mobilePhone)}
						prevBtnHidden={true}
						isLastStep={false}
					/>
				</Grid>
			</div>
		</Grid>
	);
};
